import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
import { TIER_LIMITS } from '@mockd/shared';
import { ROUTES } from '../../routes';
import { useAuth, useTeams } from '../../hooks';
import { CreateTeamModal } from '../teams';

export const TeamSwitcher: React.FC = () => {
  const { user } = useAuth();
  const { teams, fetchTeams, createTeam } = useTeams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Navbar is rendered outside any params-bearing route, so the active team
  // comes from the URL itself rather than useParams().
  const activeTeamId = location.pathname.match(/^\/teams\/([^/]+)/)?.[1] ?? null;
  const activeTeam = activeTeamId ? teams.find((t) => t.id === activeTeamId) : null;

  // Membership in other users' teams doesn't count against the cap — only
  // teams this user owns do (mirrors the server-side check in createTeam).
  const ownedTeamCount = teams.filter((t) => t.role === 'owner').length;
  const teamLimit = TIER_LIMITS[user?.tier ?? 'free'].maxTeams;
  const atTeamLimit = ownedTeamCount >= teamLimit;

  // Navbar (and this switcher) stays mounted across client-side navigation, so
  // a mount-only fetch would go stale the moment a team is created or an
  // invite accepted elsewhere and the app navigates straight into it. Refetch
  // whenever the URL's team context changes, not just once.
  useEffect(() => {
    fetchTeams();
  }, [fetchTeams, activeTeamId]);

  const closeMenu = () => (document.activeElement as HTMLElement)?.blur();

  return (
    <>
      <div className="dropdown">
        <div tabIndex={0} role="button" className="btn btn-ghost gap-1" aria-label={`Switch team (current: ${activeTeam ? activeTeam.name : 'Personal'})`}>
          <span className="max-w-[10rem] truncate">{activeTeam ? activeTeam.name : 'Personal'}</span>
          <ChevronDownIcon className="size-4" />
        </div>
        <ul tabIndex={-1} className="menu menu-sm dropdown-content bg-base-100 border border-base-300 rounded-box z-1 mt-3 w-64 p-2">
          <li>
            <Link to={ROUTES.dashboard()} onClick={closeMenu} className={!activeTeam ? 'active' : ''}>
              Personal
            </Link>
          </li>
          {teams.length > 0 && <li aria-hidden="true" />}
          {teams.map((team) => (
            <li key={team.id}>
              <Link
                to={ROUTES.teams.dashboard(team.id)}
                onClick={closeMenu}
                className={team.id === activeTeamId ? 'active' : ''}
              >
                <span className="truncate">{team.name}</span>
                <span className="badge badge-ghost badge-sm ml-auto">{team.role}</span>
              </Link>
            </li>
          ))}
          <li aria-hidden="true" />
          {activeTeam && (
            <li>
              <Link to={ROUTES.teams.settings(activeTeam.id)} onClick={closeMenu}>Team settings</Link>
            </li>
          )}
          <li>
            {atTeamLimit ? (
              <Link
                to={ROUTES.pricing()}
                onClick={closeMenu}
                className="text-base-content/70"
                title={
                  teamLimit === 0
                    ? 'Teams require a Pro plan — upgrade to create one'
                    : `You've reached your plan's limit of ${teamLimit} team${teamLimit === 1 ? '' : 's'} — upgrade for more`
                }
              >
                Create team… <span className="badge badge-ghost badge-sm ml-auto">Upgrade</span>
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  setShowCreateModal(true);
                }}
              >
                Create team…
              </button>
            )}
          </li>
        </ul>
      </div>

      <CreateTeamModal
        isOpen={showCreateModal}
        onCreate={(name) => createTeam({ name })}
        onCreated={(team) => {
          setShowCreateModal(false);
          navigate(ROUTES.teams.dashboard(team.id));
        }}
        onClose={() => setShowCreateModal(false)}
      />
    </>
  );
};
